import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faGlobe,
  faStar,
  faHeart,
  faUsers,
  faChartLine,
  faAward,
  faRocket,
  faShieldAlt,
  faLightbulb,
  faHandshake,
  faGem,
  faBullseye,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import 'swiper/css';
import { Autoplay } from 'swiper/modules';

const BrandScrolling = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const brands = [
    { 
      id: 1, 
      name: 'TechCorp Solutions', 
      category: 'Technology',
      icon: faBuilding,
      color: 'from-blue-500 to-blue-600',
      description: 'Leading tech solutions provider'
    },
    { 
      id: 2, 
      name: 'Global Media Hub', 
      category: 'Media',
      icon: faGlobe,
      color: 'from-green-500 to-green-600',
      description: 'Worldwide media network'
    },
    { 
      id: 3, 
      name: 'Innovation Labs', 
      category: 'Research',
      icon: faLightbulb,
      color: 'from-purple-500 to-purple-600',
      description: 'Cutting-edge research facility'
    },
    { 
      id: 4, 
      name: 'Digital Partners', 
      category: 'Partnership',
      icon: faHandshake,
      color: 'from-orange-500 to-orange-600',
      description: 'Strategic digital partnerships'
    },
    { 
      id: 5, 
      name: 'Media Professionals', 
      category: 'Media',
      icon: faUsers,
      color: 'from-pink-500 to-pink-600',
      description: 'Professional media services'
    },
    { 
      id: 6, 
      name: 'Growth Analytics', 
      category: 'Analytics',
      icon: faChartLine,
      color: 'from-indigo-500 to-indigo-600',
      description: 'Data-driven growth solutions'
    },
    { 
      id: 7, 
      name: 'Elite Brands', 
      category: 'Branding',
      icon: faGem,
      color: 'from-yellow-500 to-yellow-600',
      description: 'Premium brand management'
    },
    { 
      id: 8, 
      name: 'Target Marketing', 
      category: 'Marketing',
      icon: faBullseye,
      color: 'from-red-500 to-red-600',
      description: 'Precision marketing solutions'
    },
    { 
      id: 9, 
      name: 'Secure Networks', 
      category: 'Security',
      icon: faShieldAlt,
      color: 'from-teal-500 to-teal-600',
      description: 'Enterprise security solutions'
    }
  ];

  const stats = [
    { number: "500+", label: "Brand Partners", icon: faBuilding },
    { number: "50+", label: "Countries", icon: faGlobe },
    { number: "99%", label: "Success Rate", icon: faStar },
    { number: "24/7", label: "Support", icon: faHeart }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-16"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              BRAND NETWORK
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Our <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Brand</span> Partners
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover our network of trusted brands and partners who have chosen IINSAF as their platform for growth and success
            </p>
          </div>
        </div>
      </motion.div>

      {/* Brand Scrolling Section */}
      <div className="w-full max-w-7xl mx-auto mb-16">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-8"
        >
      {/* First Slider (Left to Right) */}
          <motion.div
            variants={staggerItem}
            className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Featured Partners</h2>
              <p className="text-gray-600">Leading brands in our network</p>
            </div>
            
        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          slidesPerView={6}
          loop={true}
          speed={5000}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
          }}
          className="mySwiper"
          style={{ transitionTimingFunction: 'linear !important' }}
          breakpoints={{
            320: { slidesPerView: 2, spaceBetween: 10 },
            480: { slidesPerView: 3, spaceBetween: 15 },
            768: { slidesPerView: 4, spaceBetween: 15 },
            1024: { slidesPerView: 6, spaceBetween: 20 },
          }}
        >
          {brands.map((brand) => (
                <SwiperSlide key={brand.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="group bg-white rounded-2xl shadow-lg border border-gray-100 p-6 h-[180px] flex flex-col items-center justify-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  >
                    {/* Brand Icon */}
                    <div className={`w-16 h-16 bg-gradient-to-r ${brand.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <FontAwesomeIcon icon={brand.icon} className="text-white text-xl" />
                    </div>

                    {/* Brand Name */}
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors text-center">
                      {brand.name}
                    </h3>

                    {/* Category */}
                    <div className="text-sm text-gray-500 mb-2">
                      {brand.category}
                    </div>

                    {/* Verification Badge */}
                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xs" />
                      <span className="text-xs text-green-600 font-medium">Verified</span>
                    </div>
                  </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
          </motion.div>

          {/* Second Slider (Right to Left) */}
          <motion.div
            variants={staggerItem}
            className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Growing Network</h2>
              <p className="text-gray-600">New partners joining our platform</p>
      </div>

        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          slidesPerView={7}
          loop={true}
          speed={5000}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
          }}
          className="mySwiper"
          style={{
            direction: 'rtl',
            transitionTimingFunction: 'linear !important',
          }}
          breakpoints={{
            320: { slidesPerView: 2, spaceBetween: 10 },
            480: { slidesPerView: 3, spaceBetween: 15 },
            768: { slidesPerView: 5, spaceBetween: 15 },
            1024: { slidesPerView: 7, spaceBetween: 20 },
          }}
        >
          {brands.map((brand) => (
                <SwiperSlide key={brand.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="group bg-white rounded-2xl shadow-lg border border-gray-100 p-4 h-[160px] flex flex-col items-center justify-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  >
                    {/* Brand Icon */}
                    <div className={`w-12 h-12 bg-gradient-to-r ${brand.color} rounded-xl flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <FontAwesomeIcon icon={brand.icon} className="text-white text-lg" />
                    </div>

                    {/* Brand Name */}
                    <h3 className="text-sm font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors text-center">
                      {brand.name}
                    </h3>

                    {/* Category */}
                    <div className="text-xs text-gray-500 mb-2">
                      {brand.category}
                    </div>

                    {/* Star Rating */}
                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faStar} className="text-yellow-400 text-xs" />
                      <FontAwesomeIcon icon={faStar} className="text-yellow-400 text-xs" />
                      <FontAwesomeIcon icon={faStar} className="text-yellow-400 text-xs" />
                      <FontAwesomeIcon icon={faStar} className="text-yellow-400 text-xs" />
                      <FontAwesomeIcon icon={faStar} className="text-yellow-400 text-xs" />
                    </div>
                  </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
          </motion.div>
        </motion.div>
      </div>

      {/* Stats Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Brand <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">Network</span> Impact
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Numbers that showcase our growing network and successful partnerships
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <FontAwesomeIcon icon={stat.icon} className="text-white text-xl" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BrandScrolling;